import { createSlug } from "@suse-o11y/create-slug";
import { identity } from "@suse-o11y/identity";
import type { ImmutableMap } from "@suse-o11y/immutable-map";
import { Permitted } from "@suse-o11y/permitted";
import { useCallback, useMemo } from "react";

import { type NodeId, type QueryView, Type, type Version } from "~/models/dto";

import type { Perspectives } from "../common/openapi";
import { queryPerspectives } from "../queries/perspectives";
import { useQuery } from "../queries/react-query";

import { useQueryViews } from "./use-query-views";

/**
 * Lookup a query view by its slug, which can be either the identifier or the name. The slug "explore" is reserved and will not match any saved view.
 *
 * @param slug either identifier or name of the query view to look up
 *
 * @returns the selected query view, null if no view matches the slug, or undefined if the query views are not yet loaded
 */
export function useQueryViewBySlug(slug: undefined | string): undefined | Permitted<QueryView>;

/**
 * Lookup a query view by its slug, which can be either the identifier or the name. The slug "explore" is reserved and will not match any saved view.
 * @param slug either identifier or name of the query view to look up
 * @param select optional selector to transform the resulting view
 *
 * @returns the selected result of the selector function, or undefined if the query views are not yet loaded
 */
export function useQueryViewBySlug<TResult>(
	slug: undefined | string,
	select: (view: Permitted<QueryView>) => TResult,
): undefined | TResult;

export function useQueryViewBySlug<TResult>(
	slug: undefined | string,
	select: (view: Permitted<QueryView>) => TResult = identity as typeof select,
): undefined | TResult {
	/**
	 * Memoize the selector for {@link useQueryViews} so it returns the same reference when it is passed further to {@link select}.
	 */
	const selector = useCallback(
		(views: ImmutableMap<NodeId, QueryView>) => {
			/**
			 * The {@link slug} is likely not present due to data-loading, so cascade the "loading" by returning undefined.
			 */
			if (slug == null) {
				return undefined;
			}

			const view = views.find(({ identifier, name }) => {
				if (slug === identifier) {
					return true;
				}

				// Explore is a special case, it is not a saved view
				if (slug === "explore") {
					return false;
				}

				return createSlug(name) === createSlug(slug);
			});

			/**
			 * A {@link view} is denied if it does not exist among all user's {@link views}.
			 */
			return view ? Permitted.granted(view) : Permitted.denied();
		},
		[slug],
	);

	const viewBySlug = useQueryViews(selector);

	const needsFallback = viewBySlug != null && !viewBySlug.getOrElse(undefined);

	const { data: perspectives, isError: perspectivesError } = useQuery({
		enabled: needsFallback,
		retry: false,
		staleTime: Number.POSITIVE_INFINITY,
		...queryPerspectives(slug ?? ""),
	});

	const resolvedView = useMemo(() => {
		if (viewBySlug == null) {
			return viewBySlug;
		}

		if (!needsFallback) {
			return viewBySlug;
		}

		if (perspectives) {
			return Permitted.granted(synthesizeQueryView(perspectives));
		}

		if (perspectivesError) {
			return Permitted.denied() as Permitted<QueryView>;
		}

		return undefined;
	}, [viewBySlug, needsFallback, perspectives, perspectivesError]);

	return useMemo(() => (resolvedView == null ? resolvedView : select(resolvedView)), [resolvedView, select]);
}

function synthesizeQueryView(perspectives: Perspectives): QueryView {
	const qm = perspectives.topology?.queryMetadata;

	return {
		_type: Type.QueryView,
		identifier: perspectives.identifier,
		name: perspectives.identifier,
		query: perspectives.topologyQuery,
		queryVersion: "" as Version,
		ownedBy: perspectives.readOnly ? "system" : undefined,
		groupingEnabled: qm?.groupingEnabled ?? false,
		groupedByLayers: qm?.groupedByLayer ?? false,
		groupedByDomains: qm?.groupedByDomain ?? false,
		groupedByRelations: qm?.groupedByRelation ?? false,
		showIndirectRelations: qm?.showIndirectRelations ?? false,
		minimumGroupSize: qm?.minGroupSize ?? 0,
		autoGrouping: qm?.autoGrouping ?? false,
		connectedComponents: qm?.connectedComponents ?? false,
		neighboringComponents: qm?.neighboringComponents ?? false,
	};
}
